import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Brain, Heart, Users, Sparkles, Lightbulb, AlertCircle, Shield, Zap, Moon, Activity, HeartHandshake, RefreshCw, ChevronRight, BookOpen, X } from 'lucide-react';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { Container } from '../components/brand/BrandLayout';
import { IconButton } from '../components/brand/BrandButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for dynamic icon rendering
const ICON_MAP = {
    Brain, Heart, Users, Sparkles, Lightbulb, AlertCircle, Shield,
    Zap, Moon, Activity, HeartHandshake, RefreshCw, BookOpen
};

// Topic Card - Glass morphism style
const TopicCard = ({ topic, onSelect, index }) => {
    const Icon = typeof topic.icon === 'string' ? ICON_MAP[topic.icon] : topic.icon;
    const IconComponent = Icon || Brain;
    
    const variantStyles = {
        sage: { 
            card: 'glass-card-sage hover:shadow-lg hover:shadow-[#7D8E95]/20',
            icon: 'from-[#7D8E95] to-[#1D6B6B]',
            iconGlow: 'icon-glow-sage',
            text: 'text-[#1D6B6B]'
        },
        lavender: { 
            card: 'glass-card-lavender hover:shadow-lg hover:shadow-[#7D8E95]/20',
            icon: 'from-[#7D8E95] to-[#7D8E95]',
            iconGlow: 'icon-glow-lavender',
            text: 'text-[#7D8E95]'
        },
        rose: { 
            card: 'glass-card-rose hover:shadow-lg hover:shadow-[#E8B4B8]/20',
            icon: 'from-[#E8B4B8] to-[#D4959A]',
            iconGlow: 'icon-glow-rose',
            text: 'text-[#FFBB98]'
        },
        blue: { 
            card: 'glass-card hover:shadow-lg hover:shadow-[#A8C5D9]/20',
            icon: 'from-[#A8C5D9] to-[#7BA3BD]',
            iconGlow: '',
            text: 'text-[#7D8E95]'
        }
    };
    
    const style = variantStyles[topic.variant] || variantStyles.sage;

    return (
        <button
            onClick={() => onSelect(topic)}
            className={`w-full ${style.card} rounded-2xl p-5 text-left card-hover group transition-all duration-300`}
            style={{ animationDelay: `${index * 50}ms` }}
            data-testid={`topic-${topic.id}`}
        >
            <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${style.icon} flex items-center justify-center ${style.iconGlow} flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#1D6B6B] text-base group-hover:text-[#3A3A37] transition-colors">
                        {topic.title}
                    </p>
                    <SmallText className="text-[#8B8B87] mt-0.5 line-clamp-1">
                        {topic.shortAnswer}
                    </SmallText>
                </div>
                <ChevronRight className={`w-5 h-5 ${style.text} opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300 flex-shrink-0`} />
            </div>
        </button>
    );
};

// Full Content Modal
const TopicModal = ({ topic, onClose }) => {
    const Icon = typeof topic.icon === 'string' ? ICON_MAP[topic.icon] : topic.icon;
    const IconComponent = Icon || Brain;
    
    const variantStyles = {
        sage: { icon: 'from-[#7D8E95] to-[#1D6B6B]', iconGlow: 'icon-glow-sage' },
        lavender: { icon: 'from-[#7D8E95] to-[#7D8E95]', iconGlow: 'icon-glow-lavender' },
        rose: { icon: 'from-[#E8B4B8] to-[#D4959A]', iconGlow: 'icon-glow-rose' },
        blue: { icon: 'from-[#A8C5D9] to-[#7BA3BD]', iconGlow: '' }
    };
    
    const style = variantStyles[topic.variant] || variantStyles.sage;

    // Close on escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    return (
        <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm animate-fade-in"
            onClick={onClose}
        >
            <div 
                className="w-full max-w-2xl max-h-[85vh] bg-white/70/95 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden animate-scale-up"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Modal Header */}
                <div className="sticky top-0 z-10 bg-white/70/90 backdrop-blur-md border-b border-white/40/40 p-6">
                    <div className="flex items-start gap-4">
                        <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${style.icon} flex items-center justify-center ${style.iconGlow} flex-shrink-0`}>
                            <IconComponent className="w-7 h-7 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <Heading as="h2" size="text-xl" className="text-[#1D6B6B] mb-1">
                                {topic.title}
                            </Heading>
                            <BodyText className="text-[#7D8E95]">
                                {topic.shortAnswer}
                            </BodyText>
                        </div>
                        <button
                            onClick={onClose}
                            className="w-10 h-10 rounded-full flex items-center justify-center text-[#7D8E95] hover:text-[#1D6B6B] hover:bg-[#FFBB98]/40 transition-all duration-200"
                            data-testid="close-modal-btn"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>
                
                {/* Modal Content */}
                <div className="p-6 overflow-y-auto max-h-[calc(85vh-120px)]">
                    <div className="prose prose-sm max-w-none">
                        <div className="text-[#2A8585] leading-relaxed whitespace-pre-line text-base">
                            {topic.fullContent}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const UnderstandingADHD = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [topics, setTopics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('mental-health');

    // Fetch content from API
    useEffect(() => {
        const fetchContent = async () => {
            try {
                const res = await fetch(`${API_URL}/api/educational/topics`);
                if (res.ok) {
                    const data = await res.json();
                    setTopics(data.topics || []);
                }
            } catch (error) {
                console.error('Failed to fetch educational content:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchContent();
    }, []);

    // Separate topics into categories
    const adhdTopics = topics.filter(t => 
        !['attachment-styles', 'negative-cycles', 'adhd-relationships', 'relationship-patterns', 'coregulation'].includes(t.id)
    );
    
    const relationshipTopics = topics.filter(t => 
        ['attachment-styles', 'negative-cycles', 'adhd-relationships', 'relationship-patterns', 'coregulation'].includes(t.id)
    );

    const displayedTopics = activeTab === 'mental-health' ? adhdTopics : relationshipTopics;

    return (
        <div className="min-h-screen bg-transparent">
            {/* Header */}
            <header className="sticky top-0 z-40 glass-header">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-2 sm:gap-3">
                    <IconButton 
                        onClick={() => navigate('/home')} 
                        className="text-[#7D8E95] hover:text-[#1D6B6B] hover:bg-[#FFBB98]/40 flex-shrink-0"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </IconButton>
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-[#7D8E95] to-[#7D8E95] flex items-center justify-center icon-glow-lavender flex-shrink-0">
                            <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                        </div>
                        <div className="min-w-0">
                            <Heading as="h1" size="text-base sm:text-lg" className="text-[#1D6B6B] truncate">Understanding ADHD</Heading>
                            <SmallText className="text-[#7D8E95] text-xs sm:text-sm">From confusion to clarity</SmallText>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
                {/* Hero Section */}
                <div className="text-center mb-8">
                    <Heading as="h1" size="text-2xl sm:text-3xl" className="text-[#1D6B6B] mb-4 leading-tight">
                        Understanding the impact of ADHD
                    </Heading>
                    <BodyText className="text-[#7D8E95] max-w-xl mx-auto leading-relaxed">
                        Tap any topic to learn more. Knowledge is the first step toward real change.
                    </BodyText>
                </div>

                {/* Tab Navigation */}
                <div className="flex gap-1.5 sm:gap-2 mb-6 sm:mb-8 p-1 sm:p-1.5 bg-[#FFBB98]/40 rounded-xl sm:rounded-2xl">
                    <button
                        onClick={() => setActiveTab('mental-health')}
                        className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-2.5 sm:py-3.5 px-2 sm:px-5 rounded-lg sm:rounded-xl font-medium transition-all duration-300 ${
                            activeTab === 'mental-health'
                                ? 'bg-white/90 backdrop-blur-sm text-[#1D6B6B] shadow-soft'
                                : 'text-[#7D8E95] hover:text-[#2A8585] hover:bg-white/40'
                        }`}
                        data-testid="tab-mental-health"
                    >
                        <Brain className={`w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0 transition-colors ${activeTab === 'mental-health' ? 'text-[#7D8E95]' : ''}`} />
                        <span className="text-xs sm:text-base">Mind & Emotions</span>
                    </button>
                    <button
                        onClick={() => setActiveTab('relationships')}
                        className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-2.5 sm:py-3.5 px-2 sm:px-5 rounded-lg sm:rounded-xl font-medium transition-all duration-300 ${
                            activeTab === 'relationships'
                                ? 'bg-white/90 backdrop-blur-sm text-[#1D6B6B] shadow-soft'
                                : 'text-[#7D8E95] hover:text-[#2A8585] hover:bg-white/40'
                        }`}
                        data-testid="tab-relationships"
                    >
                        <HeartHandshake className={`w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0 transition-colors ${activeTab === 'relationships' ? 'text-[#FFBB98]' : ''}`} />
                        <span className="text-xs sm:text-base">Relationships</span>
                    </button>
                </div>

                {/* Content */}
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-16">
                        <div className="w-10 h-10 border-2 border-[#7D8E95] border-t-transparent rounded-full animate-spin mb-4"></div>
                        <SmallText className="text-[#8B8B87]">Loading topics...</SmallText>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {displayedTopics.length > 0 ? (
                            displayedTopics.map((topic, index) => (
                                <TopicCard
                                    key={topic.id}
                                    topic={topic}
                                    index={index}
                                    onSelect={setSelectedTopic}
                                />
                            ))
                        ) : (
                            <div className="text-center py-12">
                                <BodyText className="text-[#8B8B87]">No topics available</BodyText>
                            </div>
                        )}
                    </div>
                )}

                {/* Bottom spacing for floating elements */}
                <div className="h-24"></div>
            </main>

            {/* Topic Detail Modal */}
            {selectedTopic && (
                <TopicModal 
                    topic={selectedTopic} 
                    onClose={() => setSelectedTopic(null)} 
                />
            )}
        </div>
    );
};

export default UnderstandingADHD;
